import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import SelectedIcon from '@material-ui/icons/CheckCircle'
import DotIcon from '@material-ui/icons/FiberManualRecord'
import SelectableIcon from '@material-ui/icons/RadioButtonUnchecked'
import type { ReactNode } from 'react'
import React, { useRef, useState } from 'react'
import type { Variant as TypographyVariant } from '@material-ui/core/styles/createTypography'
import Grid from '../Grid'
import MenuItem from '../MenuItem'
import Tooltip from '../Tooltip'
import Typography from '../Typography'
import type { DropdownSelectOption } from './DropdownSelect'

const useStyles = makeStyles<Theme, DropdownSelectMenuItemProps>((theme) => ({
  root: {
    margin: ({ searchable }) => (searchable ? theme.spacing(0.25, 2) : theme.spacing(0.25)),
    padding: ({ dense }) => (dense ? theme.spacing(1, 0.5) : theme.spacing(1, 1.5)),
    backgroundColor: ({ selected }) => (selected ? theme.palette.action.hover : 'transparent'),
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    },
    '@media (hover: none) and (pointer: coarse)': {
      '&:active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    },
    borderRadius: theme.shape.borderRadius
  },
  labelWrapper: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: ({ bold }) => (bold ? 600 : 'normal')
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '22px'
  },
  icon: {
    fontSize: '1.1rem'
  },
  dot: {
    marginRight: theme.spacing(1)
  }
}))

export interface DropdownSelectMenuItemProps {
  option: DropdownSelectOption
  selected: boolean
  showSelectableIcon?: boolean
  typographyVariant?: TypographyVariant
  searchable?: boolean
  bold?: boolean
  dense?: boolean
  formatOptionLabel?: (option: DropdownSelectOption) => ReactNode
  onClick: () => void
}

export default function DefaultDropdownSelectMenuItem(props: DropdownSelectMenuItemProps) {
  const { option, selected, typographyVariant = 'body1', showSelectableIcon, formatOptionLabel, onClick } = props
  const classes = useStyles(props)
  const labelRef = useRef<HTMLElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  const handleMouseEnter = () => {
    if (labelRef.current) {
      setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth)
    }
  }

  return (
    <MenuItem
      key={option.value}
      disabled={option.disabled}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={classes.root}
      value={option.value}
    >
      <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
        {option.dotColor && (
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '22px', marginRight: 8 }}
          >
            <DotIcon fontSize="small" style={{ color: option.dotColor }} />
          </div>
        )}
        <Grid item xs={10} className={classes.labelWrapper}>
          {formatOptionLabel && !!formatOptionLabel(option) ? (
            formatOptionLabel(option)
          ) : (
            <Tooltip title={isOverflowing ? option.label : ''} enterDelay={500}>
              <span ref={labelRef} onMouseEnter={handleMouseEnter} className={classes.label}>
                <Typography variant={typographyVariant} className={classes.label}>
                  {option.label}
                </Typography>
              </span>
            </Tooltip>
          )}
          {option.sublabel && (
            <Typography variant="caption" color="inherit">
              {option.sublabel}{' '}
            </Typography>
          )}
          {option.role && <Typography variant="caption">{option.role}</Typography>}
        </Grid>
        <div className={classes.iconContainer}>
          {selected && <SelectedIcon className={classes.icon} />}
          {showSelectableIcon && !selected && <SelectableIcon className={classes.icon} />}
        </div>
      </Grid>
    </MenuItem>
  )
}
